import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormStepProps, ApplicationFormData } from '../../types/application';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 10px;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const StepTitle = styled.h2`
  color: ${props => props.theme.primaryColor};
  font-size: 1.4rem;
  margin-bottom: 8px;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StepDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 30px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
    gap: 30px;
  }
`;

const MainForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  
  @media (min-width: 768px) {
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  height: fit-content;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  
  &.required::after {
    content: ' *';
    color: #d32f2f;
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }

  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.span`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
  
  input[type="radio"] {
    margin: 0;
  }
`;

const AddressGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 576px) {
    grid-template-columns: 1fr 1fr;
  }
  
  .full-width {
    grid-column: 1 / -1;
  }
`;

const SidebarTitle = styled.h3`
  color: ${props => props.theme.primaryColor};
  font-size: 1.1rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: #666;
    font-size: 14px;
    margin-bottom: 8px;
    padding-left: 16px;
    position: relative;

    &::before {
      content: '•';
      color: ${props => props.theme.primaryColor};
      position: absolute;
      left: 0;
    }
  }
`;

const businessTypes = [
  'Retail Shop',
  'Restaurant', 
  'Education',
  'Travel',
  'Supermarket',
  'E-commerce',
  'Healthcare',
  'Professional Services',
  'Others (not listed here)'
];

const countries = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'SG', name: 'Singapore' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' }
];

const BusinessInformation: React.FC<FormStepProps & { onValidationChange: (isValid: boolean) => void }> = ({
  data,
  updateData,
  onValidationChange
}) => {
  const {
    register,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      businessName: data.businessName,
      tradingNames: data.tradingNames,
      companyNumber: data.companyNumber,
      businessAddress: data.businessAddress,
      businessType: data.businessType,
      businessDescription: data.businessDescription,
      businessModel: data.businessModel,
      websiteOrApp: data.websiteOrApp,
      taxNumber: data.taxNumber
    }
  });

  const watchedValues = watch();

  useEffect(() => {
    updateData(watchedValues as Partial<ApplicationFormData>);
  }, [watchedValues, updateData]);

  useEffect(() => {
    // Simple validation - check required fields
    const hasRequiredFields = watchedValues.businessName && 
                             watchedValues.companyNumber && 
                             watchedValues.businessType && 
                             watchedValues.businessDescription &&
                             watchedValues.taxNumber;
    onValidationChange(!!hasRequiredFields);
  }, [watchedValues, onValidationChange]);

  return (
    <StepContainer>
      <StepTitle>Business Information</StepTitle>
      <StepDescription>
        Please provide accurate business registration details. This information will be verified against official company registries.
      </StepDescription>

      <FormGrid>
        <MainForm>
          <FormGroup>
            <Label className="required">Business Registration Name</Label>
            <Input
              {...register('businessName')}
              placeholder="Exact business name as shown on official company registry"
              hasError={!!errors.businessName}
            />
            {errors.businessName && <ErrorMessage>{errors.businessName.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Other names / Trading name(s)</Label>
            <Input
              {...register('tradingNames')}
              placeholder="Your account will only be used for one business. All other names should be listed here"
            />
            {errors.tradingNames && <ErrorMessage>{errors.tradingNames.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">Company Registration Number</Label>
            <Input
              {...register('companyNumber')}
              placeholder="Exact registration number as shown on official registry"
              hasError={!!errors.companyNumber}
            />
            {errors.companyNumber && <ErrorMessage>{errors.companyNumber.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">Business Address</Label>
            <AddressGrid>
              <div className="full-width">
                <Input
                  {...register('businessAddress.street')}
                  placeholder="Street address"
                  hasError={!!errors.businessAddress?.street}
                />
                {errors.businessAddress?.street && <ErrorMessage>{errors.businessAddress.street.message}</ErrorMessage>}
              </div>
              
              <div>
                <Input
                  {...register('businessAddress.city')}
                  placeholder="City"
                  hasError={!!errors.businessAddress?.city}
                />
                {errors.businessAddress?.city && <ErrorMessage>{errors.businessAddress.city.message}</ErrorMessage>}
              </div>
              
              <div>
                <Input
                  {...register('businessAddress.state')}
                  placeholder="State/Province (optional)"
                />
              </div>
              
              <div>
                <Input
                  {...register('businessAddress.postalCode')}
                  placeholder="Postal Code"
                  hasError={!!errors.businessAddress?.postalCode}
                />
                {errors.businessAddress?.postalCode && <ErrorMessage>{errors.businessAddress.postalCode.message}</ErrorMessage>}
              </div>
              
              <div>
                <Select
                  {...register('businessAddress.country')}
                  hasError={!!errors.businessAddress?.country}
                >
                  <option value="">Choose country ⬇️</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </Select>
                {errors.businessAddress?.country && <ErrorMessage>{errors.businessAddress.country.message}</ErrorMessage>}
              </div>
            </AddressGrid>
          </FormGroup>

          <FormGroup>
            <Label className="required">Business Type</Label>
            <Select
              {...register('businessType')}
              hasError={!!errors.businessType}
            >
              <option value="">Choose one below ⬇️</option>
              {businessTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Select>
            {errors.businessType && <ErrorMessage>{errors.businessType.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">Brief Business Description</Label>
            <TextArea
              {...register('businessDescription')}
              placeholder="This will help us to prioritise your registration process"
              hasError={!!errors.businessDescription}
            />
            {errors.businessDescription && <ErrorMessage>{errors.businessDescription.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">We are a(n)...</Label>
            <RadioGroup>
              <RadioOption>
                <input
                  type="radio"
                  {...register('businessModel')}
                  value="online"
                />
                Online only business
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  {...register('businessModel')}
                  value="offline"
                />
                Offline only business
              </RadioOption>
              <RadioOption>
                <input
                  type="radio"
                  {...register('businessModel')}
                  value="both"
                />
                Both online and offline business
              </RadioOption>
            </RadioGroup>
            {errors.businessModel && <ErrorMessage>{errors.businessModel.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Website URL / App Name</Label>
            <Input
              {...register('websiteOrApp')}
              placeholder="This will help us to prioritise your registration process"
            />
          </FormGroup>

          <FormGroup>
            <Label className="required">Tax/VAT number (if applicable)</Label>
            <Input
              {...register('taxNumber')}
              placeholder="Enter 'N/A' if you do not have a tax/VAT number"
              hasError={!!errors.taxNumber}
            />
            {errors.taxNumber && <ErrorMessage>{errors.taxNumber.message}</ErrorMessage>}
          </FormGroup>
        </MainForm>

        <Sidebar>
          <SidebarTitle>What you'll need</SidebarTitle>
          <InfoList>
            <li>Official business registration details</li>
            <li>Company registration number</li>
            <li>Complete business address</li>
            <li>Tax/VAT registration (if applicable)</li>
            <li>Website or app details (if applicable)</li>
          </InfoList>

          <SidebarTitle style={{ marginTop: '25px' }}>Important Notes</SidebarTitle>
          <InfoList>
            <li>All information must match official registries</li>
            <li>Business name should be exact as registered</li>
            <li>Complete address verification required</li>
            <li>Support for 190+ countries worldwide</li>
          </InfoList>
        </Sidebar>
      </FormGrid>
    </StepContainer>
  );
};

export default BusinessInformation;